import { apiFetch } from "@/lib/api";

/** The Django API wraps bodies as `{ success, message, data, meta }`. These
 * team endpoints return a PLAIN ARRAY (or a single row) in `data` — not
 * paginated. */
interface Envelope<T> {
  data: T;
}

/** Where a BP-team member comes from: the BP's own in-house staff, or an NSI
 * (house) user the platform assigned to this BP. */
export type MemberOrigin = "in_house" | "nsi_assigned";

/** A member on a Business-Practitioner firm's combined team. NSI-assigned rows
 * carry their `home_org` (the NSI house they belong to) and are the only rows
 * that can be unassigned here. */
export interface BpTeamMember {
  id: string;
  name: string;
  email: string;
  role: string | null;
  origin: MemberOrigin;
  home_org: { id: string; name: string } | null;
  assigned_to: { id: string; name: string } | null;
  /** The BP that owns this team row, with its short `code` identifier (shown as
   * a badge next to the member so BP ownership is clear). */
  bp: { id: string; name: string; code: string } | null;
  is_practitioner: boolean;
}

/**
 * GET /firm/business-practitioners/{firmId}/team — the BP's combined team
 * (in-house members + NSI-assigned members). Allowed for GA/SA and for a member
 * of that BP firm. User PKs are integers; `id` arrives as a string here.
 */
export async function getBpTeam(firmId: string): Promise<BpTeamMember[]> {
  const body = await apiFetch<Envelope<BpTeamMember[]>>(
    `/firm/business-practitioners/${firmId}/team`,
  );
  return body.data.map((m) => ({ ...m, id: String(m.id) }));
}

/**
 * POST /firm/business-practitioners/{firmId}/team — assign an NSI (house) user
 * to this BP. Returns the new member row (201, or 200 if already assigned).
 * GA/SA only.
 */
export async function assignNsiMember(firmId: string, userId: string): Promise<BpTeamMember> {
  const body = await apiFetch<Envelope<BpTeamMember>>(
    `/firm/business-practitioners/${firmId}/team`,
    { method: "POST", json: { user_id: userId } },
  );
  return { ...body.data, id: String(body.data.id) };
}

/**
 * DELETE /firm/business-practitioners/{firmId}/team/{userId} — unassign an
 * NSI member (204). `userId` is a numeric string (User PKs are integers).
 * GA/SA only.
 */
export async function unassignNsiMember(firmId: string, userId: string): Promise<void> {
  await apiFetch<void>(`/firm/business-practitioners/${firmId}/team/${userId}`, {
    method: "DELETE",
  });
}
